import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Chip, Grid, Pagination,
  Checkbox
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import { searchSenateJournals } from '../services/api';
import SearchSkeleton from './SearchSkeleton';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import DateHierarchyFilter from './DateHierarchyFilter';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';

export default function SenateJournalsSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [facets, setFacets] = useState({});
  const [activeFilters, setActiveFilters] = useState({});

  // Date drill-down state
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:${v}`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });
    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = activeFilters, year = selectedYear, month = selectedMonth, pageSize = perPage) => {
    const baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    const q = buildQueryWithFilters(baseQuery, filters);
    setLoading(true);
    setError(null);
    try {
      const data = await searchSenateJournals(q, newPage, pageSize, year, month);
      setResults(data);
      setPage(newPage);
      if (data.facets) setFacets(data.facets);
      const newParams = { q: baseQuery };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      setSearchParams(newParams);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search. Please try again.');
      setResults(null);
    }
    setLoading(false);
    setInitialLoading(false);
  };

  // Date drill-down handlers
  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, year, null);
  };

  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    setSelectedDay(null);
    const year = month.split('-')[0];
    setSelectedYear(year);
    handleSearch(null, 1, activeFilters, year, month);
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
  };

  const clearDateDrillDown = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, null, null);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, 1, {});
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, newSize);
  };

  const handleReset = () => {
    setQuery('');
    setResults(null);
    setPage(1);
    setActiveFilters({});
    setFacets({});
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setSearchParams({});
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <ArticleIcon sx={{ color: '#673ab7', fontSize: 32 }} />
        <Typography variant="h4">Senate Journals</Typography>
      </Box>

      <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search Senate Journals..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            variant="outlined"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
          <Button
            variant="contained"
            onClick={() => handleSearch()}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{ borderRadius: 3, px: 4, bgcolor: '#673ab7', '&:hover': { bgcolor: '#512da8' } }}
          >
            Search
          </Button>
          <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
            Reset
          </Button>
        </Box>
      </Paper>

      {initialLoading && <SearchSkeleton count={5} showFacets={true} />}

      {!initialLoading && error && (
        <ErrorState message={error} onRetry={() => handleSearch()} themeColor="#673ab7" />
      )}

      {!initialLoading && !error && results && results.total === 0 && (
        <EmptyState
          query={query}
          type="Senate journals"
          onSuggestionClick={(q) => { setQuery(q); handleSearch(q); }}
          onReset={handleReset}
          themeColor="#673ab7"
        />
      )}

      {!initialLoading && !error && results && results.total > 0 && (
        <Grid container spacing={3}>
          {(facets.years?.length > 0 || facets.months?.length > 0 || facets.days?.length > 0) && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Filter Results</Typography>
                  {(Object.keys(activeFilters).length > 0 || selectedYear) && (
                    <Button size="small" onClick={() => { clearAllFilters(); clearDateDrillDown(); }}>Clear All</Button>
                  )}
                </Box>
                <DateHierarchyFilter
                  facets={facets}
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                  selectedDay={selectedDay}
                  onYearClick={handleYearClick}
                  onMonthClick={handleMonthClick}
                  onDayClick={handleDayClick}
                  onClearAll={clearDateDrillDown}
                  themeColor="#673ab7"
                />
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} md={(facets.years?.length > 0 || facets.months?.length > 0 || facets.days?.length > 0) ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1">
                    {results.total.toLocaleString()} journals found
                  </Typography>
                  <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                </Box>
                {results.total > perPage && (
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              <List>
                {results.hits.map((hit, index) => (
                  <React.Fragment key={hit.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography
                              variant="resultTitle"
                              component={RouterLink}
                              to={`/senate-journals/${encodeURIComponent(hit.id)}`}
                              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {hit.title || 'Senate Journal'}
                            </Typography>
                            {hit.journal_number && (
                              <Chip
                                label={`No. ${hit.journal_number}`}
                                size="small"
                                sx={{ bgcolor: '#673ab7', color: 'white' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {formatDate(hit.date)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
